import * as raf from 'raf';
import React from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';

import { CenteredSpinner } from 'src/shared/components/CenteredSpinner';
import { Howler } from 'src/shared/components/Howler';
import { Triangle } from 'src/shared/styles/shared';
import { color } from 'src/shared/styles/variables';

import { PlayAudioIcon } from '../svg/PlayAudioIcon';
import { StopAudioIcon } from '../svg/StopAudioIcon';
import { AudioProgressBar } from './AudioProgressBar';
import * as S from './MusicPlayerStyles';

const formatTotalLength = (duration: number, translatedMinute: string) => {
    const lengthInMinutes = Math.round(duration / 60);

    return `${lengthInMinutes} ${translatedMinute}${lengthInMinutes > 1 ? 's' : ''}`;
};

const formatRemainingTime = (remained: number) =>
    new Date(Math.floor(remained >= 0 ? remained : 0) * 1000).toISOString().substr(11, 8);

interface Props extends WithTranslation {
    track: string;
    color?: string;
    hideTotalLength?: boolean;
    buttonOnly?: boolean;
    centered?: boolean;
    onPlay: (duration: number) => void;
    onTrackEnd: () => void;
    onListened: () => void;
}

interface State {
    duration: number;
    percentage: number;
    loaded: boolean;
    played: boolean;
    isPlaying: boolean;
    isListened: boolean;
    isRewinding: boolean;
    formattedTotalLength: string;
    formattedRemainingTime: string;
}

class MusicPlayerContainer extends React.Component<Props, State> {
    private player: any;
    private raf: any;

    public state: State = {
        duration: 0,
        loaded: false,
        played: false,
        isPlaying: false,
        isRewinding: false,
        isListened: false,
        percentage: 0,
        formattedTotalLength: '',
        formattedRemainingTime: '',
    };

    public static defaultProps: Partial<Props> = {
        onPlay: (duration: number) => duration,
        onTrackEnd: () => null,
        onListened: () => null,
    };

    public componentWillUnmount(): void {
        this.clearRAF();
    }

    private handleOnLoad = () => {
        const duration = this.player.duration;

        this.setState({
            loaded: true,
            formattedTotalLength: formatTotalLength(duration, this.props.t('common.minute')),
            duration,
        });
    };

    private handleOnPlay = () => {
        if (!this.state.played) {
            this.props.onPlay(this.state.duration);
        }

        this.setState({
            isPlaying: true,
            played: true,
        });

        this.renderSeekPos();
    };

    public handleTogglePlay = () => {
        this.setState(state => ({
            isPlaying: !state.isPlaying,
        }));

        this.player.togglePlay();
    };

    public handleOnEnd = () => {
        this.setState({
            played: false,
            isListened: false,
            percentage: 0,
        });

        this.clearRAF();

        this.props.onTrackEnd();
    };

    private renderSeekPos = () => {
        if (this.state.percentage > 50 && !this.state.isListened) {
            this.setState({ isListened: true });

            this.props.onListened();
        }

        const seek = this.player.seek;

        this.setState(state => ({
            percentage: state.percentage,
            formattedRemainingTime: formatRemainingTime(state.duration - seek),
        }));

        this.raf = raf(this.renderSeekPos);
    };

    private changeSeek = (progressPercent: number) =>
        (this.player.seek = this.state.duration * (progressPercent / 100));

    private handleDrag = (percentage: number) => this.setState({ percentage });

    private handleMouseDown = (percentage: number) =>
        this.setState({
            isRewinding: true,
            percentage,
        });

    private handleMouseUp = () => {
        if (!this.state.isRewinding) {
            return;
        }

        this.changeSeek(this.state.percentage);
        this.setState({ isRewinding: false });
    };

    private clearRAF(): void {
        raf.cancel(this.raf);
    }

    public render(): JSX.Element {
        const {
            loaded,
            isPlaying,
            isRewinding,
            percentage,
            formattedTotalLength,
            formattedRemainingTime,
        } = this.state;

        const iconColor = this.props.buttonOnly ? color.white : this.props.color;

        return (
            <>
                <S.PlayerWrapper buttonOnly={this.props.buttonOnly} color={this.props.color}>
                    <Triangle size={220} right={85} />
                    <Triangle size={220} darker right={-35} />
                    <S.PlayerContent>
                        {loaded ? (
                            <S.PlayerControls
                                buttonOnly={this.props.buttonOnly}
                                isPlaying={isPlaying}
                                centered={this.props.centered}
                            >
                                <S.PlayPauseButton
                                    buttonOnly={this.props.buttonOnly}
                                    isPlaying={isPlaying}
                                    color={this.props.color}
                                    onClick={this.handleTogglePlay}
                                >
                                    {isPlaying ? (
                                        <StopAudioIcon color={iconColor} />
                                    ) : (
                                        <PlayAudioIcon color={iconColor} />
                                    )}
                                    {!this.props.buttonOnly && (
                                        <span>
                                            {isPlaying
                                                ? this.props.t('player.pause')
                                                : this.props.t('player.play')}
                                        </span>
                                    )}
                                </S.PlayPauseButton>
                                {isPlaying || isRewinding ? (
                                    <S.PlaybackInfo>
                                        <AudioProgressBar
                                            onDrag={this.handleDrag}
                                            onMouseDown={this.handleMouseDown}
                                            onMouseUp={this.handleMouseUp}
                                            value={percentage}
                                            small={this.props.buttonOnly}
                                            isColored={this.props.buttonOnly}
                                        />
                                        <S.TimeToEnd buttonOnly={this.props.buttonOnly}>
                                            -&nbsp;
                                            {formattedRemainingTime}
                                        </S.TimeToEnd>
                                    </S.PlaybackInfo>
                                ) : (
                                    !this.props.hideTotalLength && (
                                        <S.TotalLength>{formattedTotalLength}</S.TotalLength>
                                    )
                                )}
                            </S.PlayerControls>
                        ) : (
                            <CenteredSpinner />
                        )}
                    </S.PlayerContent>
                </S.PlayerWrapper>
                <Howler
                    ref={ref => (this.player = ref)}
                    src={this.props.track}
                    isPlaying={this.state.isPlaying}
                    onLoad={this.handleOnLoad}
                    onPlay={this.handleOnPlay}
                    onEnd={this.handleOnEnd}
                />
            </>
        );
    }
}

export const MusicPlayer = withTranslation()(MusicPlayerContainer);
